function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _toPropertyKey from "@babel/runtime/helpers/esm/toPropertyKey";
import * as React from 'react';
import { useGridApiMethod } from "../../utils/useGridApiMethod.js";
/**
 * Implement the Pipeline Pattern
 *
 * More information and detailed example in (TODO add link to technical doc when ready)
 *
 * Some plugins contains custom logic to enrich data provided by other plugins or components.
 * For instance, the row grouping plugin needs to add / remove the grouping columns when the grid columns are updated.
 *
 * =====================================================================================================================
 *
 * The plugin containing the custom logic must use:
 *
 * - `useGridRegisterPipeProcessor` to register their processor.
 *
 * - `apiRef.current.requestPipeProcessorsApplication` to imperatively re-apply a group.
 *   This method should be used in last resort.
 *   Most of the time, the application should be triggered by an update on the deps of the processor.
 *
 * =====================================================================================================================
 *
 * The plugin or component that needs to enrich its data must use:
 *
 * - `apiRef.current.unstable_applyPipeProcessors` to run in chain all the processors of a given group.
 *
 * - `useGridRegisterPipeApplier` to re-apply the whole pipe when requested.
 *   The applier will be called when:
 *   * a processor is registered.
 *   * `apiRef.current.requestPipeProcessorsApplication` is called for the given group.
 */ export var useGridPipeProcessing = function(apiRef) {
    var cache = React.useRef({});
    var isRunning = React.useRef(false);
    var runAppliers = React.useCallback(function(groupCache) {
        if (isRunning.current || !groupCache) {
            return;
        }
        isRunning.current = true;
        Object.values(groupCache.appliers).forEach(function(callback) {
            callback();
        });
        isRunning.current = false;
    }, []);
    var registerPipeProcessor = React.useCallback(function(group, id, processor) {
        if (!cache.current[group]) {
            cache.current[group] = {
                processors: new Map(),
                processorsAsArray: [],
                appliers: {}
            };
        }
        var groupCache = cache.current[group];
        var oldProcessor = groupCache.processors.get(id);
        if (oldProcessor !== processor) {
            groupCache.processors.set(id, processor);
            groupCache.processorsAsArray = Array.from(cache.current[group].processors.values());
            runAppliers(groupCache);
        }
        return function() {
            cache.current[group].processors.delete(id);
            cache.current[group].processorsAsArray = Array.from(cache.current[group].processors.values());
        };
    }, [
        runAppliers
    ]);
    var registerPipeApplier = React.useCallback(function(group, id, applier) {
        if (!cache.current[group]) {
            cache.current[group] = {
                processors: new Map(),
                processorsAsArray: [],
                appliers: {}
            };
        }
        cache.current[group].appliers[id] = applier;
        return function() {
            var _appliers = cache.current[group].appliers, otherAppliers = _objectWithoutPropertiesLoose(_appliers, [
                id
            ].map(_toPropertyKey));
            cache.current[group].appliers = otherAppliers;
        };
    }, []);
    var requestPipeProcessorsApplication = React.useCallback(function(group) {
        runAppliers(cache.current[group]);
    }, [
        runAppliers
    ]);
    var applyPipeProcessors = React.useCallback(function() {
        for(var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++){
            args[_key] = arguments[_key];
        }
        var _args = _sliced_to_array(args, 3), group = _args[0], value = _args[1], context = _args[2];
        if (!cache.current[group]) {
            return value;
        }
        var processors = cache.current[group].processorsAsArray;
        var result = value;
        for(var i = 0; i < processors.length; i += 1){
            result = processors[i](result, context);
        }
        return result;
    }, []);
    var preProcessingPrivateApi = {
        registerPipeProcessor: registerPipeProcessor,
        registerPipeApplier: registerPipeApplier,
        requestPipeProcessorsApplication: requestPipeProcessorsApplication
    };
    var preProcessingPublicApi = {
        unstable_applyPipeProcessors: applyPipeProcessors
    };
    useGridApiMethod(apiRef, preProcessingPrivateApi, 'private');
    useGridApiMethod(apiRef, preProcessingPublicApi, 'public');
};
